import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/FormField';
import {
    DAYS_OF_WEEK,
    DAY_LABELS,
    isAvailabilityConfigured,
    timeToMinutes,
    type Availability,
    type DayOfWeek,
    type TimeBlock,
} from '@/lib/availability';

interface WeeklyAvailabilityEditorProps {
    value: Availability;
    onChange: (next: Availability) => void;
    /** Read-only mode — hides all inputs and action buttons. */
    disabled?: boolean;
}

export function WeeklyAvailabilityEditor({
    value,
    onChange,
    disabled = false,
}: WeeklyAvailabilityEditorProps) {
    function updateDay(day: DayOfWeek, blocks: TimeBlock[]) {
        // Rebuild from the DayOfWeek allowlist so any stray keys from a legacy
        // shape on the source record (e.g. {availDays, prefStart, prefEnd}
        // seeded before the rebuild) don't tag along and fail server validation.
        const next: Availability = {};
        for (const d of DAYS_OF_WEEK) {
            const existing = value[d];
            if (existing && existing.length > 0) next[d] = existing;
        }
        if (blocks.length === 0) {
            delete next[day];
        } else {
            next[day] = blocks;
        }
        onChange(next);
    }

    function addBlock(day: DayOfWeek) {
        const existing = value[day] ?? [];
        updateDay(day, [...existing, { start: '09:00', end: '17:00' }]);
    }

    function removeBlock(day: DayOfWeek, idx: number) {
        const existing = value[day] ?? [];
        updateDay(day, existing.filter((_, i) => i !== idx));
    }

    function patchBlock(day: DayOfWeek, idx: number, patch: Partial<TimeBlock>) {
        const existing = value[day] ?? [];
        const next = existing.map((b, i) => (i === idx ? { ...b, ...patch } : b));
        updateDay(day, next);
    }

    const configured = isAvailabilityConfigured(value);

    return (
        <div className="space-y-3">
            {!configured && (
                <p className="text-xs text-text-subtle">
                    No availability set. While empty, the scheduler won't warn about
                    sessions outside availability. Add at least one time block below to
                    enable warnings.
                </p>
            )}
            <div className="space-y-2">
                {DAYS_OF_WEEK.map((day) => {
                    const blocks = value[day] ?? [];
                    return (
                        <DayRow
                            key={day}
                            day={day}
                            blocks={blocks}
                            disabled={disabled}
                            onAdd={() => addBlock(day)}
                            onRemove={(i) => removeBlock(day, i)}
                            onPatch={(i, patch) => patchBlock(day, i, patch)}
                            configured={configured}
                        />
                    );
                })}
            </div>
        </div>
    );
}

interface DayRowProps {
    day: DayOfWeek;
    blocks: TimeBlock[];
    disabled: boolean;
    configured: boolean;
    onAdd: () => void;
    onRemove: (idx: number) => void;
    onPatch: (idx: number, patch: Partial<TimeBlock>) => void;
}

function DayRow({
    day, blocks, disabled, configured, onAdd, onRemove, onPatch,
}: DayRowProps) {
    const hasBlocks = blocks.length > 0;
    return (
        <div className="flex items-start gap-3 py-2 border-b border-border last:border-b-0">
            <div className="w-24 pt-1.5 text-sm font-medium text-text">
                {DAY_LABELS[day]}
            </div>
            <div className="flex-1 space-y-1.5">
                {!hasBlocks && (
                    <p className="text-xs text-text-subtle pt-1.5">
                        {configured ? 'Unavailable' : 'Not set'}
                    </p>
                )}
                {blocks.map((blk, i) => {
                    const bad = timeToMinutes(blk.end) <= timeToMinutes(blk.start);
                    return (
                        <div key={i} className="flex items-center gap-2">
                            <Input
                                type="time"
                                value={blk.start}
                                onChange={(e) => onPatch(i, { start: e.target.value })}
                                disabled={disabled}
                                className="w-32"
                                aria-label={`${DAY_LABELS[day]} block ${i + 1} start`}
                            />
                            <span className="text-text-subtle text-sm">to</span>
                            <Input
                                type="time"
                                value={blk.end}
                                onChange={(e) => onPatch(i, { end: e.target.value })}
                                disabled={disabled}
                                className="w-32"
                                aria-label={`${DAY_LABELS[day]} block ${i + 1} end`}
                            />
                            {bad && (
                                <span className="text-xs text-error">end must be after start</span>
                            )}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => onRemove(i)}
                                    className="text-text-subtle hover:text-error p-1"
                                    aria-label={`Remove ${DAY_LABELS[day]} block ${i + 1}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            {!disabled && (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onAdd}
                    className="shrink-0"
                >
                    <Plus size={14} /> Add block
                </Button>
            )}
        </div>
    );
}
